import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SerialNumSQL, ServiceCallPayload, ServiceCallPayloadBase } from '@interfaces/service-call';
import { LoadingStatuses } from '@interfaces/util';
import { SERVICE_CALL_DOCTYPE_TECHNOTIRE } from '@utils/constants-doctype';
import { BehaviorSubject, catchError, map, of, switchMap, tap, throwError } from 'rxjs';
import { StoreService } from './store.service';

@Injectable({
	providedIn: 'root',
})
export class ServiceCallService {
	private _ss = inject(StoreService);
	private http = inject(HttpClient);

	private readonly path = 'mobil/service-call';

	serialNumbers$ = new BehaviorSubject<SerialNumSQL[]>([]);
	serviceCallID$ = new BehaviorSubject<string>('');
	clientID$ = new BehaviorSubject<string>('');
	teudatype$ = new BehaviorSubject<number>(SERVICE_CALL_DOCTYPE_TECHNOTIRE);
	loading$ = new BehaviorSubject<LoadingStatuses>('idle');
	newServiceCallLoading$ = new BehaviorSubject<LoadingStatuses>('idle');

	imageUploadingQueue$ = new BehaviorSubject<Array<string>>([]);
	uploadedImages$ = new BehaviorSubject<Array<string>>([]);

	constructor() {}

	getSerialNumbersByClient(clientID: string) {
		clientID = encodeURIComponent(clientID);
		this.loading$.next('loading');
		return this._ss
			.get<SerialNumSQL>(`${this.path}/serial-numbers/${clientID}`)
			.pipe(switchMap(res => (typeof res === 'string' ? throwError(() => new Error('couldnt get serial numbers')) : of(res))));
	}

	handleGetSerialNumbers(clientID: string) {
		this.getSerialNumbersByClient(clientID).subscribe({
			next: serialNumbers => {
				this.loading$.next('success');
				this.clientID$.next(clientID);
				this.serialNumbers$.next(serialNumbers.recordset);
			},
			error: err => {
				this.loading$.next('error');
			},
		});
	}

	init(teudatype = SERVICE_CALL_DOCTYPE_TECHNOTIRE) {
		for (const loadingStatusSubject of [this.loading$, this.newServiceCallLoading$]) {
			loadingStatusSubject.next('idle');
		}
		this.serialNumbers$.next([]);
		this.clientID$.next('');
		this.serviceCallID$.next(`${new Date().getTime().toString()}000${this._ss.AgentIdConnected}`);
		this.teudatype$.next(teudatype);
	}

	uploadImageToServer(file: File) {
		if (!file) {
			throw new Error('no file');
		}
		const endpoint = `${this._ss.CustomerInformation.CustServer}/${this.path}/upload`;
		const formData = new FormData();
		formData.append('file', file);
		this.imageUploadingQueue$.next([...this.imageUploadingQueue$.value, file.name]);
		return this.http.post(`${endpoint}`, formData).pipe(
			tap(() => {
				const currentValue = this.imageUploadingQueue$.getValue();
				this.imageUploadingQueue$.next(currentValue.filter(name => name !== file.name));
			}),
		);
	}

	addImageToUploadedImages(imageName: string) {
		this.uploadedImages$.next([...this.uploadedImages$.value, imageName]);
	}

	resetImages() {
		this.uploadedImages$.next([]);
		this.imageUploadingQueue$.next([]);
	}

	handleSubmitNewServiceCall(data: { serialNumber: SerialNumSQL; remark: string }) {
		this.newServiceCallLoading$.next('loading');
		const { serialNumber, remark } = data;

		const body: ServiceCallPayloadBase = {
			AgentID: this._ss.AgentIdConnected,
			ClientID: this.clientID$.value,
			Company: this._ss.OneClientInfo.company || 1,
			Extra1: serialNumber.ProductId,
			Extra2: serialNumber.SerNum,
			PIC1: this.uploadedImages$.value[0] || '',
			PIC2: this.uploadedImages$.value[1] || '',
			ServiceCallNum: this.serviceCallID$.value,
			ServiceCallRemark: remark,
			TeudaType: this.teudatype$.value,
		};

		const bodyWithJSONString = { ...body, DataString: JSON.stringify(body) };

		this._ss
			.post<ServiceCallPayload>(`${this.path}/create`, bodyWithJSONString)
			.pipe(map(res => (typeof res === 'string' ? throwError(() => new Error('couldnt create service call')) : of(res))))
			.subscribe({
				next: () => {
					this.newServiceCallLoading$.next('success');
				},
				error: () => {
					this.newServiceCallLoading$.next('error');
				},
			});
	}
}
